import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Plus, Briefcase, Users, Edit, Loader2, CheckCircle2, Clock, Pause, Play, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePartnerContext } from "@/hooks/usePartnerContext";
import { useJobPosts } from "@/hooks/useJobPosts";
import { JOB_STATUS_LABELS } from "@/lib/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useState, useMemo } from "react";

const PAGE_SIZE = 20;

function matchesSearch(vaga: any, term: string): boolean {
  if (!term) return true;
  const t = term.toLowerCase();
  return (
    (vaga.title ?? "").toLowerCase().includes(t) ||
    (vaga.employment_type ?? "").toLowerCase().includes(t) ||
    (vaga.work_model ?? "").toLowerCase().includes(t) ||
    (vaga.location_city ?? "").toLowerCase().includes(t) ||
    (vaga.location_state ?? "").toLowerCase().includes(t) ||
    (vaga.description_summary ?? "").toLowerCase().includes(t)
  );
}

export default function EmpresaVagas() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgId, isLoading: ctxLoading } = usePartnerContext();
  const { posts, isLoading: postsLoading } = useJobPosts(orgId);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [page, setPage] = useState(0);

  const isLoading = ctxLoading || postsLoading;

  const filtered = useMemo(() => {
    let result = posts;
    if (statusFilter !== "todos") result = result.filter(v => v.status === statusFilter);
    const term = search.trim();
    if (term) result = result.filter((v) => matchesSearch(v, term));
    return result;
  }, [posts, search, statusFilter]);

  const resetPage = () => setPage(0);

  const activeJobs = useMemo(() => filtered.filter(v => v.status === "publicada"), [filtered]);
  const pausedJobs = useMemo(() => filtered.filter(v => v.status === "pausada"), [filtered]);
  const reviewJobs = useMemo(() => filtered.filter(v => v.status === "em_revisao"), [filtered]);
  const draftJobs = useMemo(() => filtered.filter(v => v.status === "rascunho"), [filtered]);
  const otherJobs = useMemo(() => filtered.filter(v => !["publicada", "em_revisao", "rascunho", "pausada"].includes(v.status)), [filtered]);

  const allForTable = useMemo(() => [...activeJobs, ...pausedJobs, ...reviewJobs, ...draftJobs, ...otherJobs], [activeJobs, pausedJobs, reviewJobs, draftJobs, otherJobs]);
  const totalPages = Math.max(1, Math.ceil(allForTable.length / PAGE_SIZE));
  const paged = allForTable.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const update: any = { status };
      if (status === "publicada") update.published_at = new Date().toISOString();
      const { error } = await supabase.from("job_posts").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ["job_posts"] });
      const msg = status === "pausada" ? "Vaga pausada" : status === "publicada" ? "Vaga reativada" : status === "em_revisao" ? "Vaga enviada para revisão" : "Status atualizado";
      toast({ title: msg });
    },
  });

  if (isLoading) {
    return <div className="page-container-desktop flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!orgId) {
    return (
      <div className="page-container-desktop text-center py-12">
        <p className="text-muted-foreground">Usuário parceiro sem organização configurada.</p>
      </div>
    );
  }

  return (
    <div className="page-container-desktop">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Minhas Vagas</h1>
          <p className="text-muted-foreground mt-1">Gerencie suas vagas de entrada</p>
        </div>
        <Button variant="hero" onClick={() => navigate("/empresa/vagas/nova")}><Plus className="w-5 h-5" />Nova Vaga</Button>
      </motion.div>

      {/* KPIs — always from unfiltered posts */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-success" /></div>
            <div><p className="text-2xl font-bold text-foreground">{posts.filter(v => v.status === "publicada").length}</p><p className="text-sm text-muted-foreground">Ativas</p></div>
          </div>
        </div>
        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center"><Clock className="w-5 h-5 text-yellow-500" /></div>
            <div><p className="text-2xl font-bold text-foreground">{posts.filter(v => v.status === "em_revisao").length}</p><p className="text-sm text-muted-foreground">Em Revisão</p></div>
          </div>
        </div>
        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center"><Pause className="w-5 h-5 text-muted-foreground" /></div>
            <div><p className="text-2xl font-bold text-foreground">{posts.filter(v => v.status === "pausada").length}</p><p className="text-sm text-muted-foreground">Pausadas</p></div>
          </div>
        </div>
        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><Users className="w-5 h-5 text-primary" /></div>
            <div><p className="text-2xl font-bold text-foreground">{posts.length}</p><p className="text-sm text-muted-foreground">Total</p></div>
          </div>
        </div>
      </motion.div>

      {/* Search + Status Filter */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por título, tipo, modelo, cidade..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); resetPage(); }}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); resetPage(); }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              <SelectItem value="publicada">Publicada</SelectItem>
              <SelectItem value="em_revisao">Em Revisão</SelectItem>
              <SelectItem value="pausada">Pausada</SelectItem>
              <SelectItem value="rascunho">Rascunho</SelectItem>
              <SelectItem value="encerrada">Encerrada</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {(search.trim() || statusFilter !== "todos") && (
          <p className="text-sm text-muted-foreground mt-2">{filtered.length} resultado(s) encontrado(s)</p>
        )}
      </motion.div>

      {/* Jobs table */}
      {paged.length > 0 ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="text-left p-4 font-medium text-foreground">Vaga</th>
                  <th className="text-left p-4 font-medium text-foreground hidden md:table-cell">Tipo</th>
                  <th className="text-left p-4 font-medium text-foreground">Status</th>
                  <th className="text-right p-4 font-medium text-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((vaga) => {
                  const sl = JOB_STATUS_LABELS[vaga.status] ?? { label: vaga.status, variant: "outline" as const };
                  const isPaused = vaga.status === "pausada";
                  const isActive = vaga.status === "publicada";
                  return (
                    <tr key={vaga.id} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors cursor-pointer"
                      onClick={() => navigate(`/empresa/vagas/${vaga.id}`)}>
                      <td className="p-4">
                        <p className="font-medium text-foreground">{vaga.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {vaga.work_model ?? "—"}
                          {vaga.location_city ? ` · ${vaga.location_city}` : ""}
                          {vaga.published_at ? ` · ${new Date(vaga.published_at).toLocaleDateString("pt-BR")}` : ""}
                        </p>
                      </td>
                      <td className="p-4 hidden md:table-cell"><Badge variant="secondary">{vaga.employment_type}</Badge></td>
                      <td className="p-4"><Badge variant={sl.variant}>{sl.label}</Badge></td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {isActive && (
                            <Button variant="ghost" size="icon-sm" title="Pausar vaga" disabled={updateStatus.isPending}
                              onClick={() => updateStatus.mutate({ id: vaga.id, status: "pausada" })}>
                              <Pause className="w-4 h-4 text-yellow-600" />
                            </Button>
                          )}
                          {isPaused && (
                            <Button variant="ghost" size="icon-sm" title="Reativar vaga" disabled={updateStatus.isPending}
                              onClick={() => updateStatus.mutate({ id: vaga.id, status: "publicada" })}>
                              <Play className="w-4 h-4 text-green-600" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon-sm" title="Editar"
                            onClick={() => navigate(`/empresa/vagas/${vaga.id}/editar`)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, allForTable.length)} de {allForTable.length}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" /> Anterior
                </Button>
                <span className="text-sm text-muted-foreground px-2">{page + 1} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                  Próximo <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      ) : (
        <div className="bg-muted/50 rounded-2xl p-12 text-center mt-6">
          <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          {search.trim() ? (
            <p className="text-sm text-muted-foreground">Nenhuma vaga encontrada para "{search}"</p>
          ) : (
            <>
              <h3 className="font-heading font-semibold text-foreground mb-2">Nenhuma vaga criada</h3>
              <Button variant="hero" onClick={() => navigate("/empresa/vagas/nova")}><Plus className="w-4 h-4" />Criar primeira vaga</Button>
            </>
          )}
        </div>
      )}

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
        className="text-center text-sm text-muted-foreground mt-6">
        Lembre-se: vagas de entrada não devem exigir experiência prévia.
      </motion.p>
    </div>
  );
}
